import { useState } from "react";
import { toast } from "sonner";
import {
  Workflow,
  Satellite,
  Mail,
  CheckCircle2,
  SkipForward,
  ExternalLink,
  Loader2,
  ArrowRight,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { api } from "../lib/api";

/**
 * Phase-2 partners panel for the onboarding wizard — Feb 2026.
 *
 * Shows one card per partner (AA / FR / TG / Email). Each card can be:
 *   - DONE     — green tick, no action (creds already saved)
 *   - PENDING  — "Set up now" expands an inline form; "Skip for now"
 *   - SKIPPED  — amber tick, "Revisit"
 *
 * Save actions hit the EXISTING config endpoints — same creds the
 * Settings cards write to — so there's one source of truth.
 */

const PARTNERS = [
  {
    key: "aa",
    label: "AA Roadservice",
    sub: "Pulls AA Workforms jobs automatically",
    icon: Workflow,
    accent: "bg-amber-500",
    docs: "https://workforms.link/Client/Landing",
    fields: [
      { k: "username", label: "AA Username", placeholder: "e.g. nts1" },
      { k: "password", label: "AA Password", type: "password" },
      { k: "org", label: "Tenant slug", placeholder: "nzaa", default: "nzaa" },
    ],
    saveEndpoint: "/integration/aa/config",
    testEndpoint: "/integration/aa/test",
    extraPayload: { auto_sync: true, sync_interval_seconds: 30 },
  },
  {
    key: "sn",
    label: "First Rescue (ServiceNow)",
    sub: "Pulls First Rescue work-orders",
    icon: Workflow,
    accent: "bg-indigo-600",
    docs: "https://firstrescue.service-now.com/fsmcp",
    fields: [
      { k: "instance_host", label: "Instance Host", placeholder: "firstrescue.service-now.com", default: "firstrescue.service-now.com" },
      { k: "username", label: "Integration email", type: "email", placeholder: "dispatch@yourcompany.co.nz" },
      { k: "password", label: "Password", type: "password" },
    ],
    saveEndpoint: "/integration/servicenow/config",
    testEndpoint: "/integration/servicenow/test",
    extraPayload: { auto_sync: true, sync_interval_seconds: 30 },
  },
  {
    key: "tg",
    label: "Telematics Guru",
    sub: "Live GPS from Digital Matter trackers",
    icon: Satellite,
    accent: "bg-violet-600",
    docs: "https://support.digitalmatter.com/the-telematics-guru-api",
    fields: [
      { k: "instance_host", label: "API host", placeholder: "api-apac03.telematics.guru", default: "api-apac03.telematics.guru" },
      { k: "api_key", label: "API key", type: "password", placeholder: "Paste from TG Account → Access tab" },
      { k: "org_id", label: "Organisation ID", placeholder: "e.g. 1234" },
    ],
    saveEndpoint: "/integration/telematics-guru/config",
    extraPayload: { auth_method: "api_key", auto_sync: true, sync_interval_seconds: 30 },
    hint: "If you don't have an API key, skip this step and set up Telematics Guru from Settings → Integrations (there's a Username/Password + region auto-finder there).",
  },
  {
    key: "email",
    label: "Email intake",
    sub: "Auto-create jobs from insurer / dispatcher emails",
    icon: Mail,
    accent: "bg-sky-600",
    docs: null,
    fields: [
      { k: "label", label: "Mailbox name", placeholder: "Insurer inbox" },
      { k: "imap_host", label: "IMAP host", placeholder: "imap.gmail.com" },
      { k: "imap_port", label: "IMAP port", type: "number", default: "993" },
      { k: "imap_username", label: "Email address", type: "email", placeholder: "dispatch@yourcompany.co.nz" },
      { k: "imap_password", label: "App password", type: "password", placeholder: "Gmail app password or similar" },
      { k: "folder", label: "Folder", default: "INBOX" },
    ],
    saveEndpoint: "/intake/mailboxes",
    saveMethod: "post",
    extraPayload: { enabled: true, use_ssl: true },
    hint: "For Gmail / Google Workspace, generate an app password at myaccount.google.com → Security → App passwords.",
  },
];

export default function OnboardingPartnersPanel({ state, onChange }) {
  return (
    <div className="space-y-4" data-testid="onboarding-partners">
      <header>
        <h2 className="text-lg font-bold flex items-center gap-2">
          Partner integrations
        </h2>
        <p className="text-xs text-slate-400 mt-1">
          Hook up whichever partners your business already uses. Skip any you
          don't have — you can revisit them any time from Settings.
        </p>
      </header>
      <div className="space-y-3">
        {PARTNERS.map((p) => (
          <PartnerCard
            key={p.key}
            partner={p}
            status={state.partners[p.key]}
            onChange={onChange}
          />
        ))}
      </div>
    </div>
  );
}

function PartnerCard({ partner, status, onChange }) {
  const [expanded, setExpanded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() =>
    Object.fromEntries(partner.fields.map((f) => [f.k, f.default ?? ""])),
  );

  const Icon = partner.icon;

  const save = async () => {
    setSaving(true);
    try {
      const method = partner.saveMethod === "post" ? "post" : "put";
      const payload = { ...partner.extraPayload, ...form };
      // Strip empty strings to avoid overwriting stored values with "".
      Object.keys(payload).forEach(
        (k) => (payload[k] === "" || payload[k] == null) && delete payload[k],
      );
      // Check the details work BEFORE storing them. Bad credentials used to
      // be accepted in silence here, and the only evidence was a
      // `last_sync_error` buried in Settings days later.
      if (partner.testEndpoint) {
        try {
          await api.post(partner.testEndpoint, payload);
        } catch (e) {
          toast.error(
            `${partner.label} not saved — ${e?.response?.data?.detail || "those details were rejected."}`,
            { duration: 10000 },
          );
          return;
        }
      }
      await api[method](partner.saveEndpoint, payload);
      toast.success(`${partner.label} connected`);
      // Status will flip to "done" on next poll via auto-detect.
      onChange?.();
      setExpanded(false);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail ||
          `Could not save ${partner.label}. Check the details and retry.`,
      );
    } finally {
      setSaving(false);
    }
  };

  const skip = async () => {
    try {
      await api.post(`/onboarding/skip/${partner.key}`);
      toast.success(`${partner.label} skipped — revisit any time in Settings.`);
      onChange?.();
    } catch (e) {
      toast.error("Couldn't skip — please retry");
    }
  };

  const unskip = async () => {
    try {
      await api.post(`/onboarding/unskip/${partner.key}`);
      onChange?.();
    } catch {
      /* no toast — low-stakes */
    }
  };

  const statusBadge = () => {
    if (status === "done")
      return (
        <span className="text-emerald-400 text-xs font-bold flex items-center gap-1">
          <CheckCircle2 className="h-3.5 w-3.5" /> Connected
        </span>
      );
    if (status === "skipped")
      return (
        <span className="text-amber-400 text-xs font-bold flex items-center gap-1">
          <SkipForward className="h-3.5 w-3.5" /> Skipped
        </span>
      );
    return (
      <span className="text-slate-500 text-xs font-bold">Not set up</span>
    );
  };

  return (
    <div
      className="rounded-lg border border-slate-800 bg-slate-900/80 overflow-hidden"
      data-testid={`onboarding-partner-${partner.key}`}
    >
      <div className="p-4 flex items-center gap-3">
        <div
          className={`h-10 w-10 rounded-md ${partner.accent} flex items-center justify-center shrink-0`}
        >
          <Icon className="h-5 w-5 text-white" strokeWidth={2.4} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm text-slate-100">{partner.label}</div>
          <div className="text-xs text-slate-400">{partner.sub}</div>
          <div className="mt-1">{statusBadge()}</div>
        </div>
        {status === "done" ? null : status === "skipped" ? (
          <Button
            variant="outline"
            size="sm"
            onClick={unskip}
            data-testid={`onboarding-partner-${partner.key}-revisit`}
          >
            Revisit
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={skip}
              data-testid={`onboarding-partner-${partner.key}-skip`}
              className="text-slate-400 hover:text-slate-200"
            >
              Skip
            </Button>
            <Button
              size="sm"
              onClick={() => setExpanded((v) => !v)}
              data-testid={`onboarding-partner-${partner.key}-setup`}
              className="bg-emerald-600 hover:bg-emerald-500 text-white"
            >
              {expanded ? "Close" : "Set up"}
              <ArrowRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          </div>
        )}
      </div>
      {expanded && status !== "done" && (
        <div className="border-t border-slate-800 bg-slate-950/60 p-4 space-y-3">
          {partner.docs && (
            <a
              href={partner.docs}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-sky-400 hover:underline inline-flex items-center gap-1"
            >
              Open {partner.label} console <ExternalLink className="h-3 w-3" />
            </a>
          )}
          {partner.hint && (
            <p className="text-[11px] text-amber-300 bg-amber-950/40 border border-amber-900/40 rounded-md px-2 py-1.5">
              {partner.hint}
            </p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {partner.fields.map((f) => (
              <div key={f.k}>
                <Label className="text-xs text-slate-300 uppercase tracking-wide">
                  {f.label}
                </Label>
                <Input
                  type={f.type || "text"}
                  placeholder={f.placeholder}
                  value={form[f.k] ?? ""}
                  onChange={(e) =>
                    setForm((s) => ({ ...s, [f.k]: e.target.value }))
                  }
                  data-testid={`onboarding-partner-${partner.key}-${f.k}`}
                  className="bg-slate-800 border-slate-700 text-slate-100 placeholder:text-slate-500"
                />
              </div>
            ))}
          </div>
          <div className="flex justify-end pt-2">
            <Button
              onClick={save}
              disabled={saving}
              data-testid={`onboarding-partner-${partner.key}-save`}
              className="bg-emerald-600 hover:bg-emerald-500 text-white"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                  Saving…
                </>
              ) : (
                <>Save & connect</>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
